/**
 * Cart Drawer — Advanced Skin MedSpa
 * AJAX cart drawer with full Shopify Cart API integration.
 * Initializes on DOMContentLoaded. Dispatches and listens
 * for custom events: 'cart:updated', 'cart:open'.
 */

(function () {
  'use strict';

  class CartDrawer {
    constructor() {
      // DOM references
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('.cart-drawer__overlay');
      this.panel = this.drawer.querySelector('.cart-drawer__panel');
      this.closeButtons = this.drawer.querySelectorAll('[data-cart-drawer-close]');
      this.itemsContainer = this.drawer.querySelector('[data-cart-items]');
      this.emptyState = this.drawer.querySelector('[data-cart-empty]');
      this.hasItemsElements = this.drawer.querySelectorAll('[data-cart-has-items]');
      this.countDisplay = this.drawer.querySelector('[data-cart-count]');
      this.subtotalDisplay = this.drawer.querySelector('[data-cart-subtotal]');
      this.shippingBar = this.drawer.querySelector('[data-shipping-bar]');
      this.shippingProgress = this.drawer.querySelector('[data-shipping-bar-progress]');
      this.shippingText = this.drawer.querySelector('[data-shipping-bar-text]');
      this.errorContainer = this.drawer.querySelector('[data-cart-error]');
      this.errorMessage = this.drawer.querySelector('[data-cart-error-message]');
      this.upsellContainer = this.drawer.querySelector('[data-cart-upsell-products]');

      // Config
      const config = window.cartDrawerConfig || {};
      this.freeShippingThreshold = config.freeShippingThreshold || 7500;
      this.cartUrl = config.cartUrl || '/cart';
      this.allProductsUrl = config.allProductsUrl || '/collections/all';

      // State
      this.cart = null;
      this.isOpen = false;
      this.isUpdating = false;
      this.previousFocusElement = null;

      // Bound methods
      this._onKeyDown = this._onKeyDown.bind(this);

      // Initialize
      this.bindEvents();
      this.initAddToCartInterception();
      this.updateCartCountFromPage();
    }

    /* ======================================================================
       Events
       ====================================================================== */

    bindEvents() {
      // Close buttons (X and overlay)
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Cart icon in header
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Custom events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:updated', () => {
        if (this.isOpen) {
          this.fetchCart().then(cart => this.renderCart(cart)).catch(() => {});
        } else {
          this.fetchCart().then(cart => this.updateCartCount(cart.item_count)).catch(() => {});
        }
      });

      // Quantity changes (delegated)
      if (!this.itemsContainer) return;
      this.itemsContainer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          this.onQuantityChange(qtyBtn);
        }

        const removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          this.onRemoveItem(removeBtn);
        }
      });

      // Upsell add buttons (delegated)
      if (this.upsellContainer) {
        this.upsellContainer.addEventListener('click', (e) => {
          const addBtn = e.target.closest('[data-upsell-add]');
          if (addBtn) {
            e.preventDefault();
            this.onUpsellAdd(addBtn);
          }
        });
      }
    }

    /* ======================================================================
       Add-to-Cart Interception
       ====================================================================== */

    initAddToCartInterception() {
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form.matches('form[action*="/cart/add"]')) return;

        e.preventDefault();
        this.handleAddToCart(form);
      });
    }

    async handleAddToCart(form) {
      const submitBtn = form.querySelector('[type="submit"], button:not([type="button"])');
      const originalText = submitBtn ? submitBtn.innerHTML : '';

      // Loading state
      if (submitBtn) {
        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
      }

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Could not add item to cart.');
        }

        await response.json();

        // Success state
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Added';
          submitBtn.classList.add('is-success');
          setTimeout(() => {
            submitBtn.innerHTML = originalText;
            submitBtn.classList.remove('is-success');
            submitBtn.disabled = false;
          }, 2000);
        }

        // Open drawer (which fetches and renders internally)
        document.dispatchEvent(new CustomEvent('cart:open'));

      } catch (error) {
        console.error('Add to cart error:', error);
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
        }
        this.showError(error.message);
        this.open();
      }
    }

    /* ======================================================================
       Open / Close
       ====================================================================== */

    async open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.previousFocusElement = document.activeElement;

      // Show drawer
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');

      // Fetch and render cart
      try {
        const cart = await this.fetchCart();
        this.renderCart(cart);
      } catch (error) {
        console.error('Error fetching cart:', error);
        this.showError('Unable to load cart. Please try again.');
      }

      // Focus management
      document.addEventListener('keydown', this._onKeyDown);
      setTimeout(() => {
        const closeBtn = this.drawer.querySelector('[data-cart-drawer-close]:not(.cart-drawer__overlay)');
        if (closeBtn) closeBtn.focus();
      }, 100);
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');

      // Release focus
      document.removeEventListener('keydown', this._onKeyDown);
      if (this.previousFocusElement) {
        this.previousFocusElement.focus();
        this.previousFocusElement = null;
      }
    }

    /* ======================================================================
       Cart API
       ====================================================================== */

    async fetchCart() {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to fetch cart.');

      this.cart = await response.json();
      return this.cart;
    }

    async updateItem(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      // Set loading on the item
      const itemEl = this.itemsContainer.querySelector(`[data-item-key="${key}"]`);
      if (itemEl) itemEl.classList.add('is-loading');

      this.hideError();

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Could not update item.');
        }

        const cart = await response.json();
        this.cart = cart;
        this.renderCart(cart);
        document.dispatchEvent(new CustomEvent('cart:updated'));

      } catch (error) {
        this.showError(error.message);
        try {
          const cart = await this.fetchCart();
          this.renderCart(cart);
        } catch (_) { /* silent */ }
      } finally {
        this.isUpdating = false;
      }
    }

    async addItem(variantId, quantity = 1) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: quantity }],
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Could not add item.');
        }

        await response.json();
        const cart = await this.fetchCart();
        this.renderCart(cart);
        document.dispatchEvent(new CustomEvent('cart:updated'));

      } catch (error) {
        console.error('Add item error:', error);
        this.showError(error.message);
      }
    }

    /* ======================================================================
       Event Handlers
       ====================================================================== */

    onQuantityChange(btn) {
      const key = btn.dataset.itemKey;
      const direction = btn.dataset.qtyChange;
      const actionsEl = btn.closest('.cart-drawer__item-actions');
      if (!actionsEl) return;
      const qtyEl = actionsEl.querySelector('[data-qty-value]');
      if (!qtyEl) return;
      const currentQty = parseInt(qtyEl.textContent, 10);
      const newQty = direction === 'plus' ? currentQty + 1 : Math.max(0, currentQty - 1);

      if (newQty === 0) {
        this.updateItem(key, 0);
      } else {
        this._debouncedUpdate(key, newQty);
      }
    }

    onRemoveItem(btn) {
      const key = btn.dataset.removeItem;
      this.updateItem(key, 0);
    }

    onUpsellAdd(btn) {
      const variantId = parseInt(btn.dataset.upsellAdd, 10);
      if (!variantId) return;

      btn.classList.add('is-loading');
      btn.textContent = 'Adding...';

      this.addItem(variantId, 1).then(() => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.textContent = 'Add';
        }, 2000);
      }).catch(() => {
        btn.classList.remove('is-loading');
        btn.textContent = 'Add';
      });
    }

    /* ======================================================================
       Rendering
       ====================================================================== */

    renderCart(cart) {
      this.cart = cart;
      const hasItems = cart.item_count > 0;

      // Toggle empty/has-items states
      if (this.emptyState) {
        this.emptyState.hidden = hasItems;
      }
      this.hasItemsElements.forEach(el => {
        el.hidden = !hasItems;
      });

      // Update count
      this.updateCartCount(cart.item_count);

      // Update subtotal
      if (this.subtotalDisplay) {
        this.subtotalDisplay.textContent = this.formatMoney(cart.total_price);
      }

      // Shipping bar
      this.updateShippingBar(cart.total_price);

      // Render items
      if (hasItems) {
        this.itemsContainer.innerHTML = cart.items.map(item => this.renderItem(item)).join('');
      }
    }

    renderItem(item) {
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>`
        : '';

      const imageUrl = item.featured_image && item.featured_image.url
        ? this.getSizedImageUrl(item.featured_image.url, '160x160')
        : item.image
          ? this.getSizedImageUrl(item.image, '160x160')
          : '';

      const safeUrl = this.escapeHtml(item.url);
      const safeKey = this.escapeHtml(item.key);

      const imageMarkup = imageUrl
        ? `<a href="${safeUrl}" tabindex="-1"><img src="${imageUrl}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="80" height="80"></a>`
        : '<div style="width:100%;height:100%;background:var(--color-surface-dark);"></div>';

      return `
        <div class="cart-drawer__item" data-item-key="${safeKey}">
          <div class="cart-drawer__item-media">
            ${imageMarkup}
          </div>
          <div class="cart-drawer__item-details">
            <h3 class="cart-drawer__item-title">
              <a href="${safeUrl}">${this.escapeHtml(item.product_title)}</a>
            </h3>
            ${variantTitle}
            <p class="cart-drawer__item-price">${this.formatMoney(item.original_price)}</p>
            <div class="cart-drawer__item-actions">
              <div class="cart-drawer__item-quantity">
                <button
                  class="cart-drawer__item-qty-btn"
                  data-qty-change="minus"
                  data-item-key="${safeKey}"
                  aria-label="Decrease quantity for ${this.escapeHtml(item.product_title)}"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <span class="cart-drawer__item-qty-value" data-qty-value>${item.quantity}</span>
                <button
                  class="cart-drawer__item-qty-btn"
                  data-qty-change="plus"
                  data-item-key="${safeKey}"
                  aria-label="Increase quantity for ${this.escapeHtml(item.product_title)}"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
              <span class="cart-drawer__item-line-total">${this.formatMoney(item.final_line_price)}</span>
            </div>
          </div>
          <button
            class="cart-drawer__item-remove"
            data-remove-item="${safeKey}"
            aria-label="Remove ${this.escapeHtml(item.product_title)} from cart"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      `;
    }

    renderEmpty() {
      if (this.emptyState) this.emptyState.hidden = false;
      this.hasItemsElements.forEach(el => { el.hidden = true; });
      if (this.itemsContainer) this.itemsContainer.innerHTML = '';
    }

    updateShippingBar(totalCents) {
      if (!this.shippingBar || !this.shippingProgress || !this.shippingText) return;

      const threshold = this.freeShippingThreshold;
      const remaining = Math.max(0, threshold - totalCents);
      const progress = Math.min(100, (totalCents / threshold) * 100);

      this.shippingProgress.style.width = progress + '%';

      if (remaining <= 0) {
        this.shippingBar.classList.add('cart-drawer__shipping-bar--complete');
        this.shippingText.innerHTML = 'You\'ve earned free shipping! &#127881;';
      } else {
        this.shippingBar.classList.remove('cart-drawer__shipping-bar--complete');
        this.shippingText.textContent = `You're ${this.formatMoney(remaining)} away from free shipping!`;
      }
    }

    updateCartCount(count) {
      // Update all cart count badges across the page
      document.querySelectorAll('[data-cart-count-badge]').forEach(badge => {
        badge.textContent = count;
        badge.setAttribute('data-count', count);

        // Bump animation
        badge.classList.remove('is-bumping');
        void badge.offsetWidth; // force reflow
        badge.classList.add('is-bumping');
      });

      // Update drawer title count
      if (this.countDisplay) {
        const itemWord = count === 1 ? 'item' : 'items';
        this.countDisplay.textContent = `(${count} ${itemWord})`;
      }
    }

    /* ======================================================================
       Error Handling
       ====================================================================== */

    showError(message) {
      if (!this.errorContainer || !this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorContainer.hidden = false;

      // Auto-hide after 5 seconds
      clearTimeout(this._errorTimeout);
      this._errorTimeout = setTimeout(() => this.hideError(), 5000);
    }

    hideError() {
      if (!this.errorContainer) return;
      this.errorContainer.hidden = true;
      this.errorMessage.textContent = '';
    }

    /* ======================================================================
       Focus Management (Accessibility)
       ====================================================================== */

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }

      if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    }

    trapFocus(e) {
      const focusableSelectors = 'a[href], button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"]), input:not([disabled])';
      const focusable = Array.from(this.panel.querySelectorAll(focusableSelectors)).filter(
        el => el.offsetParent !== null
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !this.panel.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !this.panel.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    releaseFocus() {
      document.removeEventListener('keydown', this._onKeyDown);
    }

    /* ======================================================================
       Helpers
       ====================================================================== */

    formatMoney(cents) {
      if (cents == null) return '$0.00';
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    getSizedImageUrl(url, size) {
      if (!url) return '';
      // Shopify image URL sizing: insert _SIZExSIZE before file extension
      const match = url.match(/^(.+)(\.\w{3,4})(\?.*)?$/);
      if (match) {
        return match[1] + '_' + size + match[2] + (match[3] || '');
      }
      return url;
    }

    debounce(fn, ms = 300) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), ms);
      };
    }

    /**
     * Debounced update to prevent rapid-fire API calls
     * when user clicks +/- quickly.
     */
    _debouncedUpdate = (() => {
      const pending = {};
      return (key, quantity) => {
        clearTimeout(pending[key]);
        pending[key] = setTimeout(() => {
          this.updateItem(key, quantity);
        }, 300);
      };
    })();

    /**
     * On initial load, try to get cart count from the page
     * without making an API call (fallback: fetch).
     */
    updateCartCountFromPage() {
      // Fetch cart to set initial count
      this.fetchCart().then(cart => {
        this.updateCartCount(cart.item_count);
      }).catch(() => {
        // Silent fail on initial load
      });
    }
  }

  /* ========================================================================
     Initialize on DOM ready
     ======================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.cartDrawer = new CartDrawer();
    });
  } else {
    window.cartDrawer = new CartDrawer();
  }
})();
